<template>
    <collapsible-panel title="Agenda" class='meeting-agenda-panel' ref="topicPanel" smallTitle>
        <fe-btn
            usage="tertiary"
            :disabled="!occurrence"
            :style="{opacity: !occurrence ? '0.3' : '1'}"
            :tooltip="!occurrence ? 'Meeting must be scheduled before adding an agenda topic.' : null"
            @click="adding = true"
        >
            Add Topic
        </fe-btn>

        <div class="topics">
            <draggable v-model="topics" @change="updateTopics">
                <topic
                    v-for="topic in topics"
                    :key="`agenda-topic-${topic.id}`"
                    :topic="topic"
                    :meeting="meeting"
                    :occurrence="occurrence"
                    @students="$emit('students', $event)"
                    @deleteTopic="destroyTopic(topic)"
                    @updateTopic="editTopic(topic)"
                    @refresh="$refs.crud.read()"
                />
            </draggable>
        </div>

        <div v-if="formLoading" class="d-flex align-center py-4">
            <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
            Loading form...
        </div>
        <smartform
            v-if="formInstance"
            v-show="!formLoading"
            :key="`form-${occurrence.id}-${meeting.form_id}-${formInstance.form_instance_id}`"
            :id="formInstance.form_instance_id"
            :template_id="formInstance.form_instance.form_id"
            hide-tools
            hide-sidebar
            @response="smartformReady = true"
        />

        <fe-dialog
            v-if="editing"
            :title="`${adding ? 'Add' : 'Edit'} Topic`"
            :acceptButtonText="adding ? 'Add' : 'Update'"
            :retainFocus="false"
            :acceptButtonDisabled="disableSave"
            @accept="saveTopic"
            @dismiss="cancelTopic"
        >
            <fe-label>Topic Description</fe-label>
            <fe-editor
                v-model="editingText"
                ref="topicEditor"
                :toolbarExtras="['|', 'bulletedlist', 'numberedlist']"
                @input="updateEditingText"
            />
        </fe-dialog>

        <fe-crud
            v-if="meeting && occurrence"
            ref="crud"
            :config="meetingAgendaCrudConfig"
            autoload
            refresh-on-config
            @read="topics = $event"
            @created="$refs.crud.read()"
            @destroyed="$refs.crud.read()"
            @updated="$refs.crud.read()"
        />

        <fe-crud
            v-if="meeting && occurrence"
            ref="crudForm"
            :config="formInstanceCrudConfig"
            autoload
            refresh-on-config
            @read="handleFormInstancesRead"
        />
    </collapsible-panel>
</template>

<script>
import Smartform from '@/components/modules/smartforms/SmartForm'
import CollapsiblePanel from '@/components/common/CollapsiblePanel'
import Topic from './Topic'
import draggable from 'vuedraggable'

export default {
    name: 'Agenda',

    components: {
        CollapsiblePanel,
        Topic,
        Smartform,
        draggable,
    },

    props: {
        meeting: {type: Object, required: true},
        occurrence: {type: Object}
    },

    data() {
        return {
            adding: false,
            updating: null,
            editingText: null,
            topics: [],
            formInstances: [],
            disableSave: false,
            formInstancesLoaded: false,
            smartformReady: false,
        }
    },

    computed: {
        formInstance() {
            return this.formInstances.length ? this.formInstances[0] : null
        },

        // true from the moment we know a form is assigned until the smartform has
        // finished its own internal load (instance lookup + form data + responses)
        formLoading() {
            return !!this.meeting.form_id && (!this.formInstancesLoaded || (!!this.formInstance && !this.smartformReady))
        },

        formInstanceCrudConfig() {
            if (this.meeting.id && this.occurrence.id) {
                let cfg = this.$_.cloneDeep(this.$models.meetingFormInstance)
                cfg.read.params.dashboard_meeting_id = this.meeting.id
                cfg.read.params.schedule_date_id = this.occurrence.id
                return cfg
            }
        },

        meetingAgendaCrudConfig() {
            if (this.meeting.id && this.occurrence.id) {
                let cfg = this.$_.cloneDeep(this.$models.meetingAgenda)
                cfg.read.params.dashboard_meeting_id = this.meeting.id
                cfg.read.params.schedule_date_id = this.occurrence.id
                return cfg
            }
        },

        editing() {
            return this.adding || this.updating
        }
    },

    watch: {
        editing(v) {
            if (v) {
                // set focus on load of modal
                setTimeout(function () {
                    this.$refs?.topicEditor.editorInstance.editing.view.focus()
                }.bind(this), 100)
            }
        },
        'meeting.form_id'(newFormId, oldFormId) {
            if (!this.$refs.crudForm) {
                return
            }

            // If meeting's form_id changed, delete any existing form instance and reload
            if (newFormId !== oldFormId) {
                this.smartformReady = false

                // Delete existing form instance if there is one (it's tied to the old form)
                if (this.formInstance) {
                    this.$refs.crudForm.destroy(this.formInstance).then(() => {
                        this.$refs.crudForm.read()
                    }).catch((err) => {
                        console.error('Failed to delete form instance:', err)
                        this.$refs.crudForm.read()
                    })
                } else {
                    this.$refs.crudForm.read()
                }
            }
        },
        'occurrence.id'() {
            // A different occurrence's form instance needs its own load cycle
            this.formInstancesLoaded = false
            this.smartformReady = false
        }
    },

    methods: {
        saveTopic() {
            if (this.adding) this.createTopic()
            else this.updateTopic()
        },

        editTopic(topic) {
            this.editingText = topic.item
            this.updating = topic
        },

        createTopic() {
            this.$refs.crud.create({
                dashboard_meeting_id: this.meeting.id,
                schedule_date_id: this.occurrence.id,
                item: this.editingText
            }).finally(() => {
                this.cancelTopic()
            })
        },

        updateTopic() {
            this.$refs.crud.update(Object.assign({}, this.editing, {
                item: this.editingText
            })).finally(() => {
                this.cancelTopic()
            })
        },

        destroyTopic(topic) {
            this.$refs.crud.destroy(topic)
        },

        cancelTopic() {
            this.adding = false
            this.updating = null
            this.editingText = null
        },

        updateEditingText(v) {
            this.disableSave = v && v.replace(/&nbsp;/g, ' ').replace(/<\/?[^>]+(>|$)/g, "").length > 500
        },

        updateTopics() {
            this.topics.forEach((topic, index) => {
                topic.rank = index
            })
            this.$refs.crud.update(this.topics)
        },

        handleFormInstancesRead(v) {
            this.formInstances = v
            this.formInstancesLoaded = true
        }
    }
}
</script>

<style lang="scss">
.meeting-agenda-panel {
    button.fe-button.v-btn {
        margin: 8px 0;
    }
}
</style>
